import React, { useState, useEffect } from 'react';
import { db } from '../../../../firebase-config';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, query, orderBy } from 'firebase/firestore';
import { notification, Button, Input, Modal, Form, Space, Card, Typography, Divider, Rate, Upload } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UpOutlined, DownOutlined, UploadOutlined } from '@ant-design/icons';
import { storage } from '../../../../firebase-config';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';

const { TextArea } = Input;
const { Title, Text } = Typography;

const ReviewsEditor = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingReview, setEditingReview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [form] = Form.useForm();

  // Fetch reviews data
  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      setLoading(true);
      const reviewsCollection = collection(db, 'googleReviews');
      const q = query(reviewsCollection, orderBy('order', 'asc'));
      const querySnapshot = await getDocs(q);
      
      const reviewsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setReviews(reviewsData);
    } catch (error) {
      console.error('Error fetching reviews:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to load reviews data.',
        placement: 'topRight',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingReview(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEdit = (review) => {
    setEditingReview(review);
    form.setFieldsValue({
      userName: review.userName,
      userReview: review.userReview,
      userRating: review.userRating,
      userImg: review.userImg,
      order: review.order
    });
    setModalVisible(true);
  };

  const handleDelete = async (reviewId) => {
    try {
      await deleteDoc(doc(db, 'googleReviews', reviewId));
      notification.success({
        message: 'Success',
        description: 'Review deleted successfully.',
        placement: 'topRight',
      });
      fetchReviews();
    } catch (error) {
      console.error('Error deleting review:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to delete review.',
        placement: 'topRight',
      });
    }
  };

  const uploadImage = async (file) => {
    try {
      setUploadingImage(true);
      const storageRef = ref(storage, `reviews/${Date.now()}_${file.name}`);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      return downloadURL;
    } catch (error) {
      console.error('Error uploading image:', error);
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageUpload = async (info) => {
    if (info.file.status === 'uploading') {
      setUploadingImage(true);
      return;
    }
    if (info.file.status === 'done') {
      try {
        const downloadURL = await uploadImage(info.file.originFileObj);
        form.setFieldsValue({ userImg: downloadURL });
        notification.success({
          message: 'Success',
          description: 'Image uploaded successfully.',
          placement: 'topRight',
        });
      } catch (error) {
        notification.error({
          message: 'Error',
          description: 'Failed to upload image.',
          placement: 'topRight',
        });
      }
    }
  };

  const handleSubmit = async (values) => {
    try {
      if (editingReview) {
        // Update existing review
        await updateDoc(doc(db, 'googleReviews', editingReview.id), {
          userName: values.userName,
          userReview: values.userReview,
          userRating: values.userRating,
          userImg: values.userImg,
          order: parseInt(values.order),
          updatedAt: new Date().toISOString()
        });
        notification.success({
          message: 'Success',
          description: 'Review updated successfully.',
          placement: 'topRight',
        });
      } else {
        // Add new review
        const newOrder = reviews.length > 0 ? Math.max(...reviews.map(r => r.order)) + 1 : 1;
        await addDoc(collection(db, 'googleReviews'), {
          userName: values.userName,
          userReview: values.userReview,
          userRating: values.userRating,
          userImg: values.userImg,
          order: newOrder,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        });
        notification.success({
          message: 'Success',
          description: 'Review added successfully.',
          placement: 'topRight',
        });
      }
      
      setModalVisible(false);
      form.resetFields();
      fetchReviews();
    } catch (error) {
      console.error('Error saving review:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to save review.',
        placement: 'topRight',
      });
    }
  };

  const handleReorder = async (fromIndex, toIndex) => {
    try {
      const newReviews = [...reviews];
      const [movedItem] = newReviews.splice(fromIndex, 1);
      newReviews.splice(toIndex, 0, movedItem);

      // Update order for all items
      const updatePromises = newReviews.map((review, index) => 
        updateDoc(doc(db, 'googleReviews', review.id), { order: index + 1 })
      );

      await Promise.all(updatePromises);
      notification.success({
        message: 'Success',
        description: 'Review order updated successfully.',
        placement: 'topRight',
      });
      fetchReviews();
    } catch (error) {
      console.error('Error reordering reviews:', error);
      notification.error({
        message: 'Error',
        description: 'Failed to reorder reviews.',
        placement: 'topRight',
      });
    }
  };

  const uploadProps = {
    name: 'file',
    beforeUpload: () => false,
    onChange: handleImageUpload,
    showUploadList: false,
  };

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto', padding: 24 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <Title level={2}>Manage Google Reviews</Title>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={handleAdd}
          size="large"
        >
          Add New Review
        </Button>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Text>Loading reviews...</Text>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {reviews.map((review, index) => (
            <Card 
              key={review.id} 
              size="small"
              style={{ border: '1px solid #d9d9d9' }}
              actions={[
                <Button 
                  icon={<UpOutlined />} 
                  onClick={() => {
                    if (index > 0) handleReorder(index, index - 1);
                  }}
                  disabled={index === 0}
                >
                  Move Up
                </Button>,
                <Button 
                  icon={<DownOutlined />} 
                  onClick={() => {
                    if (index < reviews.length - 1) handleReorder(index, index + 1);
                  }}
                  disabled={index === reviews.length - 1}
                >
                  Move Down
                </Button>,
                <Button 
                  icon={<EditOutlined />} 
                  onClick={() => handleEdit(review)}
                >
                  Edit
                </Button>,
                <Button 
                  danger 
                  icon={<DeleteOutlined />} 
                  onClick={() => handleDelete(review.id)}
                >
                  Delete
                </Button>
              ]}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8 }}>
                    <img 
                      src={review.userImg} 
                      alt={review.userName}
                      style={{ 
                        width: 40, 
                        height: 40, 
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }} 
                    />
                    <div>
                      <Title level={5} style={{ margin: 0 }}>
                        {review.userName}
                      </Title>
                      <Rate disabled defaultValue={review.userRating} style={{ fontSize: 12 }} />
                    </div>
                  </div>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    Order: {review.order}
                  </Text>
                  <Divider style={{ margin: '8px 0' }} />
                  <div 
                    style={{ 
                      maxHeight: 100, 
                      overflow: 'hidden',
                      fontSize: '14px',
                      color: '#666',
                      lineHeight: '1.5'
                    }}
                  >
                    {review.userReview}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      <Modal
        title={editingReview ? 'Edit Review' : 'Add New Review'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            userName: '',
            userReview: '',
            userRating: 5,
            userImg: '',
            order: 1
          }}
        >
          <Form.Item
            name="userName"
            label="User Name"
            rules={[{ required: true, message: 'Please enter the user name!' }]}
          >
            <Input 
              placeholder="Enter the user name..."
              style={{ fontSize: '16px' }}
            />
          </Form.Item>

          <Form.Item
            name="userReview"
            label="Review Text"
            rules={[{ required: true, message: 'Please enter the review text!' }]}
          >
            <TextArea 
              rows={4} 
              placeholder="Enter the review text..."
              style={{ fontSize: '16px' }}
            />
          </Form.Item>

          <Form.Item
            name="userRating"
            label="Rating"
            rules={[{ required: true, message: 'Please select a rating!' }]}
          >
            <Rate style={{ fontSize: 20 }} />
          </Form.Item>

          <Form.Item
            name="userImg"
            label="User Image URL"
            rules={[{ required: true, message: 'Please enter the user image URL!' }]}
          >
            <Input 
              placeholder="Enter the user image URL..."
              style={{ fontSize: '16px' }}
            />
          </Form.Item>

          <Form.Item label="Upload Image">
            <Upload {...uploadProps}>
              <Button 
                icon={<UploadOutlined />} 
                loading={uploadingImage}
              >
                Upload Image
              </Button>
            </Upload>
          </Form.Item>

          <Form.Item
            name="order"
            label="Display Order"
            rules={[{ required: true, message: 'Please enter the display order!' }]}
          >
            <Input 
              type="number" 
              min={1}
              placeholder="Enter display order (1, 2, 3...)"
            />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingReview ? 'Update Review' : 'Add Review'}
              </Button>
              <Button onClick={() => setModalVisible(false)}>
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ReviewsEditor; 